import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Bell, CalendarDays, FileText, Home, LayoutDashboard, LogOut,
  Menu, MessageSquare, Music2, Newspaper, Package, Palette, Settings,
  ShoppingBag, SlidersHorizontal, Store, Tags, UsersRound, Video, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const groups = [
  {
    label: "Contenu",
    links: [
      { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
      { to: "/admin/accueil", label: "Accueil", icon: Home },
      { to: "/admin/news", label: "News", icon: Newspaper },
      { to: "/admin/artistes", label: "Artistes", icon: Music2 },
      { to: "/admin/studio", label: "Studio", icon: SlidersHorizontal },
      { to: "/admin/videos", label: "Vidéos", icon: Video },
      { to: "/admin/medias", label: "Médias", icon: Palette },
    ],
  },
  {
    label: "Activité",
    links: [
      { to: "/admin/latdior", label: "Latdior", icon: Store },
      { to: "/admin/services", label: "Services", icon: ShoppingBag },
      { to: "/admin/reservations", label: "Réservations", icon: CalendarDays },
      { to: "/admin/commandes", label: "Commandes", icon: Package },
      { to: "/admin/messages", label: "Messages", icon: MessageSquare },
      { to: "/admin/newsletter", label: "Newsletter", icon: UsersRound },
      { to: "/admin/notifications", label: "Notifications", icon: Bell },
    ],
  },
  {
    label: "Réglages",
    links: [
      { to: "/admin/tarifs", label: "Tarifs", icon: Tags },
      { to: "/admin/pages", label: "Pages", icon: FileText },
      { to: "/admin/mentions-legales", label: "Mentions", icon: FileText },
      { to: "/admin/typographie", label: "Typographie", icon: Palette },
      { to: "/admin/parametres", label: "Paramètres", icon: Settings },
    ],
  },
];

const navLinkClass = ({ isActive }) =>
  `inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-xs font-bold uppercase transition ${
    isActive ? "border-red-500 text-white" : "border-transparent text-zinc-400 hover:text-white"
  }`;

export default function AdminNavigation() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-black/95 text-white backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-[1600px] items-center justify-between gap-4 px-4 md:px-8">
        <Link to="/admin" className="flex min-w-0 items-center gap-3" aria-label="Administration Unlimited Session">
          <img src="/logo.png" alt="" className="h-8 w-auto shrink-0" />
          <span className="hidden border-l border-white/20 pl-3 text-sm font-bold uppercase text-zinc-300 sm:block">Administration</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/" className="rounded border border-white/15 px-3 py-2 text-xs font-bold uppercase transition hover:bg-white hover:text-black">
            Voir le site
          </Link>
          {user && <button type="button" onClick={logout} className="hidden rounded border border-white/15 p-2 transition hover:bg-white hover:text-black md:grid" title="Déconnexion" aria-label="Déconnexion"><LogOut size={17} /></button>}
          <button type="button" onClick={() => setOpen(true)} className="grid rounded border border-white/15 p-2 transition hover:bg-white hover:text-black md:hidden" aria-label="Ouvrir le menu admin" aria-expanded={open}><Menu size={20} /></button>
        </div>
      </div>

      <nav aria-label="Administration" className="scrollbar-hide mx-auto hidden max-w-[1600px] overflow-x-auto px-5 md:flex">
        {groups.flatMap((group) => group.links).map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={navLinkClass}>
            <Icon size={15} aria-hidden="true" />{label}
          </NavLink>
        ))}
      </nav>

      {open && createPortal(<div className="fixed inset-0 z-[100] h-dvh md:hidden">
        <button type="button" className="absolute inset-0 bg-black/75" onClick={() => setOpen(false)} aria-label="Fermer le menu admin" />
        <div className="absolute right-0 top-0 flex h-full w-[min(88vw,380px)] flex-col border-l border-white/15 bg-zinc-950 shadow-2xl">
          <div className="flex h-16 items-center justify-between border-b border-white/10 px-5">
            <span className="text-sm font-black uppercase">Administration</span>
            <button type="button" onClick={() => setOpen(false)} className="grid h-10 w-10 place-items-center rounded border border-white/15" aria-label="Fermer"><X size={20} /></button>
          </div>
          <nav aria-label="Menu admin" className="flex-1 overflow-y-auto px-4 py-5">
            {groups.map((group) => <section key={group.label} className="mb-5">
              <h2 className="mb-2 px-2 text-[11px] font-bold uppercase text-red-400">{group.label}</h2>
              <div className="space-y-1">
                {group.links.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-3 text-sm font-semibold transition ${isActive ? "bg-white text-black" : "text-zinc-300 hover:bg-white/10 hover:text-white"}`}>
                  <Icon size={18} aria-hidden="true" />{label}
                </NavLink>)}
              </div>
            </section>)}
          </nav>
          {user && <button type="button" onClick={() => { setOpen(false); logout(); }} className="flex items-center gap-3 border-t border-white/10 px-7 py-5 text-sm font-bold uppercase text-zinc-300"><LogOut size={18} />Déconnexion</button>}
        </div>
      </div>, document.body)}
    </header>
  );
}
